import React, { useState } from 'react';
import { TrendingUp, Users, Calendar, Target } from 'lucide-react';

const GymLeaderboard = ({ gyms }) => {
  const [sortBy, setSortBy] = useState('reservations');

  if (!gyms || gyms.length === 0) {
    return (
      <div className="bg-white shadow rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Gym Leaderboard</h3>
        <p className="text-sm text-gray-500">No gym data available</p>
      </div>
    );
  }

  const sortedGyms = [...gyms].sort((a, b) => {
    switch (sortBy) {
      case 'reservations':
        return b.reservationCount - a.reservationCount;
      case 'users':
        return b.uniqueUsers - a.uniqueUsers;
      case 'utilization':
        return b.utilizationRate - a.utilizationRate;
      default:
        return 0;
    }
  });

  return (
    <div className="bg-white shadow rounded-lg">
      <div className="px-6 py-5 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium text-gray-900">Gym Leaderboard</h3>

          {/* Sort Options */}
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-500">Sort by:</span>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="text-sm border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="reservations">Reservations</option>
              <option value="users">Unique Users</option>
              <option value="utilization">Utilization Rate</option>
            </select>
          </div>
        </div>
      </div>

      <div className="overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Rank
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Gym
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                <div className="flex items-center">
                  <Calendar className="w-4 h-4 mr-1" />
                  Reservations
                </div>
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                <div className="flex items-center">
                  <Users className="w-4 h-4 mr-1" />
                  Unique Users
                </div>
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                <div className="flex items-center">
                  <Target className="w-4 h-4 mr-1" />
                  Utilization
                </div>
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {sortedGyms.map((gym, index) => {
              const isTopThree = index < 3;
              const rankColors = ['text-yellow-600', 'text-gray-400', 'text-orange-600'];

              return (
                <tr key={gym.id} className={isTopThree ? 'bg-blue-50' : ''}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className={`text-lg font-bold ${isTopThree ? rankColors[index] : 'text-gray-700'}`}>
                      {index + 1}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{gym.name}</div>
                    <div className="text-sm text-gray-500">{gym.location}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-semibold text-gray-900">
                      {gym.reservationCount.toLocaleString()}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-semibold text-gray-900">
                      {gym.uniqueUsers.toLocaleString()}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="text-sm font-semibold text-gray-900">
                        {gym.utilizationRate.toFixed(1)}%
                      </div>
                      {gym.utilizationRate >= 80 && (
                        <TrendingUp className="ml-1 w-4 h-4 text-green-500" />
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      gym.isActive
                        ? 'bg-green-100 text-green-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {gym.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default GymLeaderboard;
